// ---------------------------------------------------------------------------
// Flow-steps op dispatcher — routes user step-buffer mutations through the
// MCP bus so the undo middleware records them (F4).
//
// The 11 user-facing step mutators in flow-slice.ts used to be called
// directly from UI handlers, which made every step edit invisible to the
// inverse-command undo stack: Cmd+Z either no-opped or undid an unrelated
// MCP op, and the inspector trash destroyed whole branch subtrees
// irreversibly within one auto-save debounce. UI call sites now go through
// dispatchFlowStepsOp, which dispatches `cogo.studio.editFlowSteps` — the
// tool snapshots the buffer, applies the SAME slice mutator, and returns a
// `cogo.studio.restoreFlowSteps` inverse (register-studio-tools.ts).
//
// SYNCHRONICITY CONTRACT (e2e-load-bearing, #986 QA-2a): mcpBus.execute()
// runs the registered handler synchronously before its first real await —
// validation middleware → undo middleware → CogoToolRegistry.callTool all
// call straight through (`await f()` evaluates `f()` synchronously), and the
// editFlowSteps handler body is synchronous — so the mutator has ALREADY run
// when this function returns. Call sites therefore keep their selection
// updates synchronous right after the un-awaited dispatch (e.g.
// add-step-popover sets the selection immediately after dispatching
// addStep). Pinned by mcp/__tests__/edit-flow-steps-undo.test.ts.
//
// MERGE-NAME CONTRACT: the undo middleware debounce-merges entries whose
// mergeKey (tool + params.blockName + params.name) matches within 300ms,
// keeping the FIRST inverse and the LAST forward. That is only correct for
// wholesale-set ops replayed at the same address — updateStep (params
// replaced at a path), updateStepFields (same field keys at a path), and
// migrateStepAction (action+params set at a path). Structural ops
// (add/remove/reorder/insert/case edits) must NEVER merge: a merged redo
// replays only the last forward, silently dropping the earlier mutation —
// so they get a per-call unique suffix, mirroring the middleware's own
// `uniq:` convention for identity-less commands.
// ---------------------------------------------------------------------------

import { mcpBus } from '@/mcp/studio-mcp-bus'
import type { McpResult } from '@/mcp/types'
import { useFlowEditorStore } from '@/features/flow-editor/store/flow-editor-store'
import { useStudioStore } from '@/store/studio-store'
import { resolveFlowOwner, type OwnedBlock } from '@/features/flow-editor/services/flow-ownership'
import { invalidateFlowVerification } from '@/features/flow-editor/lib/flow-verification'

/** The full user-edit mutator surface of flow-slice.ts — the whitelist the
 * editFlowSteps tool dispatches against. Seed/recovery ops (setFlowSteps,
 * recoverFlowSteps, ackFlowStepsSeed) are deliberately absent: hydrations
 * are not user edits and must never enter the undo stack. */
export const FLOW_STEP_OPS = [
  'addStep',
  'removeStep',
  'updateStep',
  'updateStepFields',
  'migrateStepAction',
  'addSwitchCase',
  'renameSwitchCase',
  'removeSwitchCase',
  'reorderSteps',
  'insertNestedStep',
  'removeNestedStep',
] as const

export type FlowStepsOp = (typeof FLOW_STEP_OPS)[number]

export function isFlowStepsOp(value: unknown): value is FlowStepsOp {
  return typeof value === 'string' && (FLOW_STEP_OPS as readonly string[]).includes(value)
}

/** Ops whose debounce-merge is redo-safe (see MERGE-NAME CONTRACT above). */
const MERGEABLE_OPS: ReadonlySet<FlowStepsOp> = new Set([
  'updateStep',
  'updateStepFields',
  'migrateStepAction',
])

let uniqSeq = 0
let warnedDirectFallback = false

function buildMergeName(op: FlowStepsOp, args: unknown[], flowId: string): string {
  if (!MERGEABLE_OPS.has(op)) return `${flowId}/${op}#${++uniqSeq}`
  const path = Array.isArray(args[0]) ? (args[0] as unknown[]).join('.') : String(args[0])
  // updateStepFields patches individual scalar fields: only commits touching
  // the SAME field set may merge (a merged redo replays only the last patch,
  // which would drop a different field's change).
  const fieldKeys =
    op === 'updateStepFields' && args[1] != null && typeof args[1] === 'object'
      ? `:${Object.keys(args[1] as Record<string, unknown>).sort().join(',')}`
      : ''
  return `${flowId}/${op}@${path}${fieldKeys}`
}

/** Direct store dispatch — the pre-F4 behavior, kept as a graceful fallback
 * for environments where the studio tools are not registered on the bus
 * (component tests render inspector/popover surfaces without
 * registerStudioTools()) or no flow is selected. Mutations still apply; they
 * are just not undoable. */
function applyDirect(op: FlowStepsOp, args: unknown[]): void {
  const store = useFlowEditorStore.getState()
  const beforeRef = store.flowSteps
  ;(store[op] as unknown as (...mutatorArgs: unknown[]) => void)(...args)
  // RUX-05 parity with the editFlowSteps handler: a non-no-op fallback edit
  // must also drop the edited flow's recorded preview traces — the
  // verification badge cannot tell which path mutated the buffer, so a
  // stale 'Verified' would survive exactly in no-tools environments.
  if (useFlowEditorStore.getState().flowSteps === beforeRef) return
  const flowId = store.flowStepsFlowId ?? store.selectedFlowId
  const flowName = store.flows.find((flow) => flow.id === flowId)?.name
  if (flowName) invalidateFlowVerification(flowName)
}

/**
 * Dispatch one step-buffer mutation through the MCP bus (undoable), resolving
 * flowId / owner blockName / merge name from the stores once for all call
 * sites. Fire-and-forget at call sites — the mutator applies synchronously
 * inside execute() (see SYNCHRONICITY CONTRACT) — but the promise is
 * returned so tests can await the full middleware round-trip.
 */
export function dispatchFlowStepsOp(op: FlowStepsOp, args: unknown[]): Promise<McpResult | null> {
  const flowStore = useFlowEditorStore.getState()
  const flowId = flowStore.selectedFlowId
  const toolRegistered = mcpBus
    .getManifest()
    .some((tool) => tool.name === 'cogo.studio.editFlowSteps')

  if (flowId == null || !toolRegistered) {
    if (!warnedDirectFallback) {
      warnedDirectFallback = true
      console.warn(
        '[flow-steps-dispatch] cogo.studio.editFlowSteps unavailable ' +
          `(${flowId == null ? 'no flow selected' : 'tool not registered'}) — ` +
          'applying the mutation directly; this edit will NOT be undoable.',
      )
    }
    applyDirect(op, args)
    return Promise.resolve(null)
  }

  // Owner blockName mirrors the auto-save bridge routing (resolveFlowOwner,
  // else the first block). It serves two masters: the undo middleware's
  // mergeKey only consults params.name when params.blockName is present, and
  // the validation middleware checks the block exists — resolveFlowOwner only
  // returns blocks present in the store, so both pass. A zero-block project
  // omits it (mergeKey degrades to non-merging `uniq:`, validation skips).
  const flowName = flowStore.flows.find((flow) => flow.id === flowId)?.name
  const blocks = (useStudioStore.getState().currentProject?.blocks ?? {}) as Record<
    string,
    OwnedBlock
  >
  const ownerBlockName =
    (flowName ? resolveFlowOwner(blocks, flowName)?.blockName : undefined) ??
    Object.keys(blocks)[0]

  return mcpBus.executeTrustedUi({
    tool: 'cogo.studio.editFlowSteps',
    params: {
      flowId,
      ...(ownerBlockName !== undefined ? { blockName: ownerBlockName } : {}),
      name: buildMergeName(op, args, flowId),
      op,
      args,
    },
  })
}
